import type { StateStorage } from 'zustand/middleware'

const DB_NAME = 'bytebeam-store'
const DB_VERSION = 1
const OBJECT_STORE = 'keyval'
/** `indexedDB.open` never settles when another tab blocks an upgrade, so cap the wait. */
const OPEN_TIMEOUT_MS = 5000

let dbPromise: Promise<IDBDatabase> | null = null

function isIndexedDbAvailable(): boolean {
	// Present but unusable in some privacy modes, where `open` throws rather than returning.
	return typeof indexedDB !== 'undefined' && indexedDB !== null
}

/** Drop the cached connection so the next operation opens a fresh one. */
function releaseDb(): void {
	const pending = dbPromise
	dbPromise = null
	pending
		?.then(db => {
			db.close()
		})
		.catch(() => {
			// Already failed to open; nothing to close.
		})
}

function openDb(): Promise<IDBDatabase> {
	if (dbPromise) {
		return dbPromise
	}

	const opening = new Promise<IDBDatabase>((resolve, reject) => {
		let settled = false
		const settle = (action: () => void) => {
			if (settled) {
				return
			}
			settled = true
			clearTimeout(timer)
			action()
		}
		const timer = setTimeout(
			() => settle(() => reject(new Error(`IndexedDB open timed out after ${OPEN_TIMEOUT_MS}ms`))),
			OPEN_TIMEOUT_MS,
		)

		let request: IDBOpenDBRequest
		try {
			request = indexedDB.open(DB_NAME, DB_VERSION)
		} catch (error) {
			settle(() => reject(error))
			return
		}

		request.onupgradeneeded = () => {
			if (!request.result.objectStoreNames.contains(OBJECT_STORE)) {
				request.result.createObjectStore(OBJECT_STORE)
			}
		}
		// Another tab holds an older version open and won't let the upgrade through.
		request.onblocked = () => settle(() => reject(new Error('IndexedDB upgrade blocked by another tab')))
		request.onerror = () => settle(() => reject(request.error))
		request.onsuccess = () =>
			settle(() => {
				const db = request.result
				// Another tab is upgrading: hold the connection open and we block it indefinitely.
				db.onversionchange = () => releaseDb()
				// Connection dropped underneath us (storage evicted, tab discarded) — reopen next time.
				db.onclose = () => {
					if (dbPromise === opening) {
						dbPromise = null
					}
				}
				resolve(db)
			})
	})

	// Never cache a rejected promise: one transient failure would disable persistence for the whole
	// session. Compare identity so a late rejection can't clear a newer connection.
	opening.catch(() => {
		if (dbPromise === opening) {
			dbPromise = null
		}
	})

	dbPromise = opening
	return opening
}

/** Errors that mean "connection is stale" rather than "this operation is invalid". */
function isStaleConnection(error: unknown): boolean {
	const name = (error as { name?: string } | null)?.name
	return name === 'InvalidStateError' || name === 'TransactionInactiveError' || name === 'NotFoundError'
}

function runTransaction<T>(
	db: IDBDatabase,
	mode: IDBTransactionMode,
	fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T> {
	if (!db.objectStoreNames.contains(OBJECT_STORE)) {
		return Promise.reject(new Error(`IndexedDB object store "${OBJECT_STORE}" is missing`))
	}
	return new Promise<T>((resolve, reject) => {
		const tx = db.transaction(OBJECT_STORE, mode)
		let result: T
		const request = fn(tx.objectStore(OBJECT_STORE))
		request.onsuccess = () => {
			result = request.result
		}
		request.onerror = () => reject(request.error)
		// Resolve on commit, not on request success. A write can succeed and still be rolled back when
		// the transaction aborts (quota exceeded is the common case), which would otherwise be
		// reported to the caller as saved.
		tx.oncomplete = () => resolve(result)
		tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'))
		tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'))
	})
}

async function withStore<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
	try {
		return await runTransaction(await openDb(), mode, fn)
	} catch (error) {
		if (!isStaleConnection(error)) {
			throw error
		}
		// The cached connection was closing or the store vanished; one retry on a fresh connection.
		releaseDb()
		return runTransaction(await openDb(), mode, fn)
	}
}

/**
 * Zustand `persist` storage backed by IndexedDB instead of localStorage — for stores whose
 * persisted payload can grow large (e.g. full stream schemas), where localStorage's ~5MB quota and
 * synchronous API become a problem.
 *
 * Every operation degrades to a no-op rather than throwing: persistence is a cache, and losing it
 * must never break the store, which keeps working purely in memory. That covers jsdom test
 * environments, privacy modes that expose `indexedDB` but refuse to open it, exhausted quota, and
 * connections dropped by storage eviction.
 */
export const indexedDbStorage: StateStorage = {
	getItem: async (name: string): Promise<string | null> => {
		if (!isIndexedDbAvailable()) {
			return null
		}
		try {
			const value = await withStore<unknown>('readonly', store => store.get(name))
			// Anything not a string was written by something else, or is a half-written record.
			return typeof value === 'string' ? value : null
		} catch (error) {
			console.error(`[indexedDbStorage] Failed to read "${name}":`, error)
			return null
		}
	},
	setItem: async (name: string, value: string): Promise<void> => {
		if (!isIndexedDbAvailable()) {
			return
		}
		try {
			await withStore<IDBValidKey>('readwrite', store => store.put(value, name))
		} catch (error) {
			const quotaExceeded = (error as { name?: string } | null)?.name === 'QuotaExceededError'
			console.error(`[indexedDbStorage] Failed to write "${name}"${quotaExceeded ? ' (quota exceeded)' : ''}:`, error)
		}
	},
	removeItem: async (name: string): Promise<void> => {
		if (!isIndexedDbAvailable()) {
			return
		}
		try {
			await withStore<undefined>('readwrite', store => store.delete(name))
		} catch (error) {
			console.error(`[indexedDbStorage] Failed to remove "${name}":`, error)
		}
	},
}

/**
 * Gives every scope its own record in the underlying storage.
 *
 * Multi-tenant caches must not share a key. A persisted payload outlives a project switch, so a
 * single key means one project's data rehydrates under another. Keying by scope also means
 * switching back and forth keeps both projects' caches warm instead of trampling one with the other.
 *
 * The scope is read per operation rather than captured once, so it always reflects where the app
 * actually is. Callers must re-run the store's `rehydrate()` when the scope changes — `persist`
 * reads storage only at store creation.
 */
export function scopedStorage(storage: StateStorage, getScope: () => string): StateStorage {
	const scopedKey = (name: string) => `${name}::${getScope()}`
	return {
		getItem: name => storage.getItem(scopedKey(name)),
		setItem: (name, value) => storage.setItem(scopedKey(name), value),
		removeItem: name => storage.removeItem(scopedKey(name)),
	}
}
